import { Link } from 'react-router-dom';
import { lookAtThatApi, type LookAtThatCalibration } from '../../lib/lookAtThatApi';
import { formatAppDateTime } from '../../lib/localTime';

interface Props {
  runId: string;
  calibration: LookAtThatCalibration | null;
  calibrating: boolean;
  running: boolean;
  executing: boolean;
  onCalibrate: () => void;
}

/**
 * Calibration atlas: CUA follows the Settings calibration brief to map chrome,
 * then snapshots the walk + idle base game. Re-running overwrites the atlas.
 */
export function CalibrationCard({ runId, calibration, calibrating, running, executing, onCalibrate }: Props) {
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Calibration</h3>
        <span className="spacer" />
        {calibration && (
          <span className="muted small">
            {calibration.complete === false && (
              <span style={{ color: 'var(--warn, #b45309)', fontWeight: 600, marginRight: 8 }}>
                Incomplete
              </span>
            )}
            captured {formatAppDateTime(calibration.capturedAt)}
          </span>
        )}
        <button
          type="button"
          className="btn small btn-primary"
          disabled={!running || executing}
          onClick={onCalibrate}
          title="Walk the chrome using the Settings calibration brief (needs an open session)"
        >
          {calibrating ? 'Calibrating…' : calibration ? 'Re-calibrate' : 'Calibrate'}
        </button>
        <Link to="/settings/calibration" className="btn small" title="Edit the calibration brief">
          Brief
        </Link>
      </div>
      {!calibration ? (
        <p className="muted small" style={{ margin: 0 }}>
          No atlas yet. Calibrate once the game is loaded: the bot follows the
          calibration brief (Settings → Look at that calibration) to map menus
          and look-alike icons, then snapshots the base game. Every later task
          starts with that map.{' '}
          <Link to="/settings/calibration">Edit the brief</Link>.
        </p>
      ) : (
        <>
          {calibration.complete === false && (
            <p className="muted small" style={{ margin: '0 0 8px' }}>
              {calibration.reason
                ? calibration.reason
                : 'The walk did not open hamburger/help. Re-calibrate once the game is idle.'}
            </p>
          )}
          <div className="look-at-that-gallery" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {calibration.screens.map((s) => (
              <figure key={s.id} className="look-at-that-gallery-item">
                <img
                  src={lookAtThatApi.calibrationImageUrl(runId, s.file)}
                  alt={s.label ?? s.id}
                  style={{ height: 180 }}
                />
                <figcaption>
                  <strong>{s.label ?? s.id}</strong>
                </figcaption>
              </figure>
            ))}
          </div>
          {calibration.controls.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Identified controls ({calibration.controls.length})
              </summary>
              <ul className="small" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {calibration.controls.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
